import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { hideAll } from 'tippy.js';

import { CollapsibleBlock } from '@components/common';
import Table from '@components/common/Table/Table';

import { assetCartRequestAvailableListSelector } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import { setAssetOwnerId, setAssetType, setSearchValue, setSort } from '@slices/assetCartRequestAvailableList';

import { setFilterUtil } from '@utils/filterUtil';

import { IGroupOperationWithActionDTO } from '@interfaces/common/defaultDTO';
import { TSelectOption } from '@interfaces/components/componentTypes';
import { ISortBy, ITableSearch, ITableSort, TTableFilter } from '@interfaces/components/table';
import { IAssetCartRequestShortView } from '@interfaces/rent/assetRent';

import { MIN_SCROLL_BOTTOM_HEIGHT } from '@constants/common';
import { searchTooltipText } from '../constants';

import { getColumns } from '../manageAssetCartRequestColumns';

import CollapsibleLabel from '../CollapsibleLabel';

import styles from './AssetCartRequestAvailable.module.scss';

interface IAssetCartRequestAvailableInterface {
	isCollapsibleBlockOpen: boolean;
	hasNextPage?: boolean;
	availableAssetRequestCount: number;
	getMoreAssets: () => void;
	toggleCollapsibleBlock: (name: string) => void;
	handleDeleteAssets: (data: IGroupOperationWithActionDTO) => void;
	isLoadingAssetCartRequest: boolean;
	ownersOptions: {
		value: string;
		name: string;
	}[];
	assetTypesOptions: {
		value: string;
		label: string;
	}[];
	assetCartRequestAvailableList: Array<IAssetCartRequestShortView>;
}

const AssetCartRequestAvailable = ({
	availableAssetRequestCount,
	isCollapsibleBlockOpen,
	toggleCollapsibleBlock,
	getMoreAssets,
	handleDeleteAssets,
	isLoadingAssetCartRequest,
	ownersOptions,
	assetTypesOptions,
	assetCartRequestAvailableList,
	hasNextPage,
}: IAssetCartRequestAvailableInterface) => {
	const dispatch = useAppDispatch();

	const [selectedAssetOwnerId, setSelectedAssetOwnerId] = useState<TSelectOption | null>(null);
	const [selectedAssetType, setSelectedAssetType] = useState<TSelectOption | null>(null);
	const [checkedIds, setCheckedIds] = useState<Array<string>>([]);

	const {
		assetOwnerId,
		assetType,
		searchValue,
		sortFieldName,
		sortDirection,
	} = useAppSelector(assetCartRequestAvailableListSelector);

	const columns = useMemo(() => getColumns(), []);

	const onScrollHandler = useCallback((e: React.UIEvent) => {
		const allVisibleHeightWithScroll = e.currentTarget.scrollTop + e.currentTarget.clientHeight;
		const heightForScroll = e.currentTarget.scrollHeight - MIN_SCROLL_BOTTOM_HEIGHT;

		const isGetMoreAssets = !isLoadingAssetCartRequest && hasNextPage
			&& (allVisibleHeightWithScroll > heightForScroll);

		if (isGetMoreAssets) {
			getMoreAssets();
		}

		hideAll();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		isLoadingAssetCartRequest,
		assetCartRequestAvailableList,
	]);

	const handleSort = (sortBy: Array<ISortBy>) => {
		dispatch(setSort({
			sortDirection: !!sortBy[0]?.desc,
			sortFieldName: sortBy[0]?.id,
		}));
	};

	const sort: ITableSort = {
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort,
	};

	const onSearchHandler = useCallback((searchValue: string) => dispatch(setSearchValue({ searchValue })), [dispatch]);

	const search: Array<ITableSearch> = useMemo(() => [
		{
			initialValue: searchValue,
			name: 'searchValue',
			handler: onSearchHandler,
			tooltipText: searchTooltipText,
			placeholder: searchTooltipText,
		},
	], [searchValue, onSearchHandler]);

	const filters: Array<TTableFilter> = useMemo(() => [
		{
			name: 'assetOwnerId',
			placeholder: 'Asset Owner',
			option: selectedAssetOwnerId,
			options: ownersOptions,
			width: '166px',
			maxMenuHeight: 150,
			setState: setSelectedAssetOwnerId,
			defaultValue: assetOwnerId,
		},
		{
			name: 'assetType',
			placeholder: 'Asset type',
			option: selectedAssetType,
			options: assetTypesOptions,
			width: '166px',
			maxMenuHeight: 150,
			setState: setSelectedAssetType,
			defaultValue: assetType,
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
	], [
		selectedAssetOwnerId,
		selectedAssetType,
		ownersOptions,
		assetTypesOptions,
		assetOwnerId,
		assetType,
	]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedAssetOwnerId,
			currentValue: assetOwnerId,
			setAction: setAssetOwnerId,
			fieldName: 'assetOwnerId',
			dispatch,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedAssetOwnerId]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedAssetType,
			currentValue: assetType,
			setAction: setAssetType,
			fieldName: 'assetType',
			dispatch,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedAssetType]);

	useEffect(() => {
		if (checkedIds.length > 0) {
			handleDeleteAssets({ ids: checkedIds });

			setCheckedIds([]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [checkedIds.length]);

	return (
		<div className={styles.assetCartRequestAvailableContainer}>
			<CollapsibleBlock
				isOpen={isCollapsibleBlockOpen}
				label={<CollapsibleLabel label={`Available (${availableAssetRequestCount})`} />}
				name='availableForRequest'
				toggleState={toggleCollapsibleBlock}
			>
				<div className={styles.assetsListTableContainer}>
					<Table
						className={styles.assetListTable}
						columns={columns}
						data={assetCartRequestAvailableList}
						filters={filters}
						isLoading={isLoadingAssetCartRequest}
						isOpenFilters={!!(assetOwnerId || assetType)}
						isOpenSearch={!!searchValue.length}
						isWithPagination={false}
						isWithoutDeleteActionMenu
						onScrollHandler={onScrollHandler}
						search={search}
						searchThreshold={0}
						searchWidth='244px'
						setCheckedIds={setCheckedIds}
						sort={sort}
					/>
				</div>
			</CollapsibleBlock>
		</div>
	);
};

export default memo(AssetCartRequestAvailable);
